<template>
  <div class="chatbot-page">

    <!-- ════════════════════════════════════════════
         USER LIST PANEL
    ════════════════════════════════════════════ -->
    <div class="list-panel" :style="isMobile ? {} : { width: listPanelWidth + 'px' }">

      <!-- Header -->
      <div class="list-header">
        <div class="d-flex align-items-center" style="gap:8px">
          <h5 class="mb-0 font-weight-bold">Chats</h5>
          <span v-if="totalUnread > 0" class="unread-total">
            {{ totalUnread > 99 ? '99+' : totalUnread }}
          </span>
          <span v-if="connectionStatus" class="connection-badge"
            :class="connectionStatus === 'connected' ? 'badge-connected' : 'badge-error'">
            <span class="dot" />{{ connectionStatus === 'connected' ? 'Live' : 'Error' }}
          </span>
        </div>
        <div class="d-flex align-items-center" style="gap:4px">
          <b-button variant="flat-secondary" size="sm" class="btn-icon" :disabled="loading" @click="loadUsers" v-b-tooltip.hover title="Refresh">
            <feather-icon icon="RefreshCwIcon" size="15" :class="loading ? 'spin' : ''" />
          </b-button>
          <div class="tag-manager-wrap" @click.stop>
            <b-button variant="flat-secondary" size="sm" class="btn-icon" @click.stop="toggleTagManager" v-b-tooltip.hover title="Manage Tags">
              <feather-icon icon="TagIcon" size="15" />
            </b-button>
            <!-- Tag manager dropdown -->
            <div v-if="showTagManager" class="tag-manager-panel">
              <div class="tag-manager-header">
                <span class="font-weight-bold" style="font-size:0.9rem">Manage Tags</span>
                <button class="tag-manager-close" @click="showTagManager = false">×</button>
              </div>
              <div class="tag-list-manage">
                <div v-if="tagsLoading" class="text-center py-2">
                  <b-spinner small variant="primary" />
                </div>
                <div v-else-if="tags.length === 0" class="text-muted small text-center py-2">No tags yet</div>
                <div v-for="tag in tags" :key="tag.id" class="tag-manage-item">
                  <span class="tag-pill" :style="{ background: tag.color }">{{ tag.name }}</span>
                  <button class="tag-delete-btn" @click="deleteTag(tag.id)" title="Delete tag">×</button>
                </div>
              </div>
              <div class="tag-create-form">
                <div class="tag-color-swatches">
                  <button
                    v-for="c in TAG_COLORS" :key="c"
                    class="tag-swatch"
                    :style="{ background: c, outline: newTagColor === c ? '2.5px solid ' + c : 'none', outlineOffset: '3px' }"
                    @click="newTagColor = c"
                  />
                  <label
                    class="tag-swatch tag-swatch-custom"
                    :style="isCustomTagColor ? { background: newTagColor, outline: '2.5px solid ' + newTagColor, outlineOffset: '3px' } : {}"
                    v-b-tooltip.hover title="Custom colour"
                  >
                    <feather-icon v-if="!isCustomTagColor" icon="PlusIcon" size="12" />
                    <input type="color" v-model="newTagColor" class="tag-swatch-custom-input" />
                  </label>
                </div>
                <div class="tag-create-row">
                  <input
                    v-model="newTagName"
                    class="tag-create-input"
                    placeholder="Tag name"
                    maxlength="20"
                    @keydown.enter.prevent="createTag"
                  />
                  <button class="tag-create-btn" :disabled="!newTagName.trim()" @click="createTag">Add</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Search -->
      <div class="list-search">
        <b-spinner v-if="contentSearching" small variant="primary" class="list-search__icon" />
        <feather-icon v-else icon="SearchIcon" size="14" class="list-search__icon" />
        <input v-model="searchQuery" class="list-search__input" placeholder="Search name or messages..." />
      </div>

      <!-- Tag filter bar -->
      <div v-if="tags.length > 0" class="tag-filter-bar">
        <button
          class="tag-filter-btn"
          :class="tagFilterId === null ? 'tag-filter-btn--all-active' : ''"
          @click="tagFilterId = null"
        >All</button>
        <button
          v-for="tag in tags" :key="tag.id"
          class="tag-filter-btn"
          :style="tagFilterId === tag.id ? { background: tag.color, color: '#fff', borderColor: tag.color } : {}"
          :class="tagFilterId === tag.id ? 'tag-filter-btn--active' : ''"
          @click="tagFilterId = tagFilterId === tag.id ? null : tag.id"
        >{{ tag.name }}</button>
      </div>

      <!-- Contact list -->
      <div class="contact-list">
        <div v-if="filteredUsers.length === 0" class="contact-empty">
          <feather-icon icon="UsersIcon" size="28" class="mb-50 text-muted" />
          <div>No users found.</div>
        </div>
        <div
          v-for="item in filteredUsers" :key="item.user_id"
          class="contact-item"
          :class="{
            'contact-item--active': selectedUser && selectedUser.user_id === item.user_id,
            'contact-item--unread': item.unread_count > 0,
          }"
          @click="openHistory(item)"
        >
          <div class="contact-avatar-wrap">
            <b-avatar
              :src="item.picture_url || null"
              :text="avatarInitial(contactDisplayName(item))"
              size="46" variant="light-primary"
            />
            <span v-if="item.unread_count > 0" class="unread-badge">
              {{ item.unread_count > 99 ? '99+' : item.unread_count }}
            </span>
          </div>
          <div class="contact-body">
            <div class="contact-row1">
              <span class="contact-name">
                <feather-icon v-if="getLinkedCustomer(item.user_id)" icon="UserCheckIcon" size="12" class="contact-name__linked-icon" v-b-tooltip.hover :title="`LINE: ${item.display_name || 'Unknown'}`" />
                {{ contactDisplayName(item) }}
              </span>
              <span class="contact-date">{{ formatDate(item.updated_at) }}</span>
            </div>
            <div class="contact-row2">
              <span class="mode-badge" :class="item.mode === 'agent' ? 'mode-agent' : 'mode-bot'">
                <span class="mode-dot" />Bot
              </span>
              <span
                v-for="tag in getUserTagObjects(item.user_id).slice(0, 2)"
                :key="tag.id"
                class="tag-pill tag-pill--sm"
                :style="{ background: tag.color }"
              >{{ tag.name }}</span>
              <span v-if="getUserTagObjects(item.user_id).length > 2" class="tag-more">
                +{{ getUserTagObjects(item.user_id).length - 2 }}
              </span>
              <span class="contact-uid">{{ item.user_id }}</span>
            </div>
            <div v-if="searchQuery.trim() && matchSnippet(item.user_id)" class="contact-match-snippet">
              <feather-icon icon="MessageSquareIcon" size="10" class="mr-25 flex-shrink-0" />
              <span class="contact-match-snippet__text">{{ matchSnippet(item.user_id) }}</span>
              <span v-if="matchCount(item.user_id) > 1" class="contact-match-count">{{ matchCount(item.user_id) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer: user count + auto-refresh countdown -->
      <div class="list-footer">
        <small class="text-muted">{{ users.length }} user{{ users.length !== 1 ? 's' : '' }}</small>
        <small class="auto-refresh-indicator">
          <span class="auto-refresh-dot" :class="loading ? 'auto-refresh-dot--loading' : 'auto-refresh-dot--live'" />
          <span v-if="loading">Refreshing…</span>
          <span v-else>Auto refresh in {{ countdown }}s</span>
        </small>
      </div>
    </div>

    <!-- Drag handle (desktop only, always visible) -->
    <div class="resize-handle" @mousedown="startResize" />

    <!-- ════════════════════════════════════════════
         CHAT HISTORY PANEL
    ════════════════════════════════════════════ -->
    <div
      class="history-panel"
      :class="{ 'is-open': showHistoryPanel }"
    >
      <!-- Bottom-sheet pull handle (mobile only) -->
      <div class="sheet-handle d-block d-md-none" @click="toggleHistoryPanel" />

      <!-- Empty state: no user selected -->
      <div v-if="!selectedUser" class="history-no-selection">
        <feather-icon icon="MessageCircleIcon" size="40" class="text-primary mb-1" />
        <p class="font-weight-bold mb-25">Chat History</p>
        <small class="text-muted">Select a user from the list to view their conversation</small>
      </div>

      <template v-else>
        <!-- Sidebar header -->
        <div class="history-header">
          <div class="d-flex align-items-center" style="gap:10px;min-width:0">
            <b-avatar
              :src="selectedUser.picture_url || null"
              :text="avatarInitial(contactDisplayName(selectedUser))"
              size="36" variant="light-primary" class="flex-shrink-0"
            />
            <div style="min-width:0">
              <div class="d-flex align-items-center flex-wrap" style="gap:6px;row-gap:2px">
                <span class="font-weight-bold text-truncate" style="max-width:160px">
                  {{ contactDisplayName(selectedUser) }}
                </span>
                <!-- Tags assigned to this user -->
                <div v-if="getUserTagObjects(selectedUser.user_id).length > 0" class="header-tags">
                  <span
                    v-for="tag in getUserTagObjects(selectedUser.user_id)"
                    :key="tag.id"
                    class="tag-pill tag-pill--sm"
                    :style="{ background: tag.color }"
                  >{{ tag.name }}</span>
                </div>
              </div>
              <!-- Linked customer info -->
              <div v-if="getLinkedCustomer(selectedUser.user_id)" class="linked-customer-info">
                <feather-icon icon="UserCheckIcon" size="11" class="mr-25" />
                <span>LINE: {{ selectedUser.display_name || 'Unknown' }}</span>
              </div>
            </div>
          </div>
          <div class="d-flex align-items-center" style="gap:4px">
            <!-- Create customer button -->
            <b-button
              v-if="!getLinkedCustomer(selectedUser.user_id)"
              variant="flat-secondary"
              size="sm"
              class="btn-icon"
              @click="$refs.createCustomerModal.open()"
              v-b-tooltip.hover
              title="Create customer from this chat"
            >
              <feather-icon icon="UserPlusIcon" size="16" />
            </b-button>
            <!-- Save search button -->
            <b-button
              variant="flat-secondary"
              size="sm"
              class="btn-icon"
              @click="$refs.saveSearchModal.open()"
              v-b-tooltip.hover
              title="Save search & notify on new listings"
            >
              <feather-icon
                icon="BookmarkIcon"
                size="16"
                :fill="hasSavedSearch(selectedUser.user_id) ? '#ffc107' : 'none'"
                :style="hasSavedSearch(selectedUser.user_id) ? { color: '#ffc107' } : {}"
              />
            </b-button>
            <!-- Tag assignment button -->
            <div class="tag-assign-wrap" @click.stop>
              <b-button
                variant="flat-secondary"
                size="sm"
                class="btn-icon"
                @click.stop="toggleTagPopup"
                v-b-tooltip.hover
                title="Tag this user"
              >
                <feather-icon
                  icon="TagIcon"
                  size="16"
                  :fill="getUserTagObjects(selectedUser.user_id).length > 0 ? '#7367f0' : 'none'"
                  :style="getUserTagObjects(selectedUser.user_id).length > 0 ? { color: '#7367f0' } : {}"
                />
              </b-button>
              <div v-if="showTagPopup" class="tag-assign-dropdown">
                <div class="tag-assign-header">Assign Tags</div>
                <div v-if="tags.length === 0" class="text-muted small text-center" style="padding:8px 12px">
                  No tags yet. Create them using the <feather-icon icon="TagIcon" size="12" /> button in the list panel.
                </div>
                <label v-for="tag in tags" :key="tag.id" class="tag-assign-item">
                  <input
                    type="checkbox"
                    :checked="isUserTagged(selectedUser.user_id, tag.id)"
                    @change="toggleUserTag(selectedUser.user_id, tag.id)"
                  />
                  <span class="tag-pill" :style="{ background: tag.color }">{{ tag.name }}</span>
                </label>
              </div>
            </div>
            <b-button variant="flat-secondary" size="sm" class="btn-icon" @click="closeHistory">
              <feather-icon icon="XIcon" size="18" />
            </b-button>
          </div>
        </div>

        <!-- Search match navigator -->
        <div v-if="activeMatches.length > 0" class="search-nav">
          <feather-icon icon="SearchIcon" size="12" class="search-nav__icon" />
          <span class="search-nav__count">{{ activeMatchIndex + 1 }} / {{ activeMatches.length }}</span>
          <div class="search-nav__btns">
            <button class="search-nav__btn" @click="goToMatch(-1)" v-b-tooltip.hover title="Previous match">
              <feather-icon icon="ChevronUpIcon" size="15" />
            </button>
            <button class="search-nav__btn" @click="goToMatch(1)" v-b-tooltip.hover title="Next match">
              <feather-icon icon="ChevronDownIcon" size="15" />
            </button>
          </div>
        </div>

        <!-- Mode banner -->
        <div class="mode-banner" :class="selectedUser.mode === 'agent' ? 'mode-banner--agent' : 'mode-banner--bot'">
          <span class="mode-banner__label">
            <feather-icon :icon="selectedUser.mode === 'agent' ? 'UserIcon' : 'CpuIcon'" size="13" class="mr-50" />
            {{ selectedUser.mode === 'agent' ? 'Currently in manual chat' : 'Currently using auto-responses' }}
          </span>
          <button
            class="mode-banner__btn"
            @click="setMode(selectedUser.user_id, selectedUser.mode !== 'agent')"
          >
            {{ selectedUser.mode === 'agent' ? 'Use auto-responses' : 'Use manual chat' }}
          </button>
        </div>

        <!-- Customer profile (built by the bot's update_profile tool) -->
        <div v-if="profileRows.length" class="cust-profile">
          <button class="cust-profile__toggle" @click="profileOpen = !profileOpen">
            <feather-icon icon="ClipboardIcon" size="13" class="mr-50" />
            <span class="flex-grow-1 text-left">Customer profile</span>
            <feather-icon :icon="profileOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="15" />
          </button>
          <div v-if="profileOpen" class="cust-profile__body">
            <div v-for="row in profileRows" :key="row.label" class="cust-profile__row">
              <span class="cust-profile__label">{{ row.label }}</span>
              <span class="cust-profile__value">{{ row.value }}</span>
            </div>
            <div class="cust-profile__foot">
              <span class="text-muted">Updated {{ formatDate(customerProfile.updated_at) }}</span>
              <button class="cust-profile__reset" @click="resetProfile">Reset</button>
            </div>
          </div>
        </div>

        <!-- Chat messages -->
        <div ref="chatBody" class="history-body">
          <div v-if="historyLoading" class="text-center py-3">
            <b-spinner small variant="primary" />
            <div class="text-muted small mt-50">Loading messages…</div>
          </div>

          <div v-else-if="chatMessages.length === 0" class="empty-history">
            <feather-icon icon="MessageSquareIcon" size="32" class="text-muted mb-50" />
            <div class="text-muted small">No messages yet</div>
          </div>

          <div v-else class="msg-list">
            <div
              v-for="(msg, i) in chatMessages" :key="i"
              :ref="'msgRow' + i"
              class="msg-row"
              :class="[msg.role === 'user' ? 'msg-row--user' : 'msg-row--bot', { 'msg-row--highlight': highlightedMsgIndex === i }]"
            >
              <!-- Avatar -->
              <div class="msg-avatar">
                <b-avatar
                  v-if="msg.role === 'user'"
                  :src="selectedUser.picture_url || null"
                  :text="avatarInitial(contactDisplayName(selectedUser))"
                  size="28" variant="light-secondary"
                />
                <b-avatar
                  v-else
                  size="28"
                  :src="msg.agentAvatar || (!msg.agentInitial ? BOT_AVATAR : null)"
                  :variant="msg.agentInitial ? 'light-success' : 'light-primary'"
                  :class="{ 'bot-avatar' : !msg.agentInitial }"
                  :text="(msg.agentAvatar || !msg.agentInitial) ? null : msg.agentInitial"
                  v-b-tooltip.hover
                  :title="msg.agentName || 'Bot'"
                />
              </div>

              <div class="msg-content" :class="msg.role === 'user' ? 'msg-content--user' : 'msg-content--bot'">
                <div v-if="msg.agentName" class="msg-agent-name">{{ msg.agentName }}</div>
                <div class="msg-bubble" :class="msg.role === 'user' ? 'bubble-user' : 'bubble-bot'">
                  <div
                    v-if="msg.quotedMessageId"
                    class="msg-quote"
                    :class="{ 'msg-quote--clickable': quotedPreview(msg) }"
                    @click="scrollToQuoted(msg)"
                  >
                    <div class="msg-quote__bar" />
                    <div class="msg-quote__body">
                      <div class="msg-quote__sender">{{ quotedPreview(msg) ? quotedPreview(msg).sender : 'Original message' }}</div>
                      <div class="msg-quote__text">{{ quotedPreview(msg) ? quotedPreview(msg).text : 'Not available in this view' }}</div>
                    </div>
                  </div>
                  <ChatImage v-if="msg.type === 'image'" :message-id="msg.imageMessageId" />
                  <div v-else class="msg-text">{{ msg.text.trim() }}</div>
                </div>
                <div v-if="msg.time" class="msg-time" :class="msg.role === 'user' ? 'msg-time--left' : 'msg-time--right'">
                  {{ msg.time }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Sidebar footer: send message -->
        <div class="history-footer">
          <!-- Emoji picker -->
          <div class="emoji-wrap" @click.stop>
            <button class="emoji-trigger" type="button" @click="toggleEmoji">😊</button>
            <div v-if="showEmoji" class="emoji-picker">
              <div class="emoji-grid">
                <button
                  v-for="(e, i) in emojiList" :key="i"
                  class="emoji-btn" type="button"
                  @click="insertEmoji(e)"
                >{{ e }}</button>
              </div>
            </div>
          </div>

          <b-form-input
            ref="sendInput"
            v-model="sendText"
            placeholder="Enter your message"
            class="send-input"
            @keydown.enter.prevent="sendMessage"
          />
          <b-button
            variant="primary"
            class="send-btn"
            :disabled="!sendText.trim() || sending"
            @click="sendMessage"
          >
            <b-spinner v-if="sending" small />
            <span v-else>Send</span>
          </b-button>
        </div>
      </template>
    </div>

    <!-- Mobile backdrop -->
    <div v-if="showHistoryPanel" class="d-block d-md-none history-backdrop" @click="toggleHistoryPanel" />

    <!-- Save search modal -->
    <save-search-modal
      ref="saveSearchModal"
      :selected-user="selectedUser"
      @saved="onSavedSearchChanged"
      @error="msg => showToast(msg, 'danger')"
    />

    <!-- Create customer modal -->
    <create-customer-modal
      ref="createCustomerModal"
      :selected-user="selectedUser"
      @created="onCustomerCreated"
      @error="msg => showToast(msg, 'danger')"
    />
  </div>
</template>

<script>
import axios from '@axios'
import chatbotAxios from '@/libs/chatbot-axios'
import { requestFCMToken, onFCMMessage } from '@/libs/firebase'
import { getUserData } from '@/auth/utils'
import {
  BCard, BButton, BTable, BAvatar, BBadge, BPagination, BSpinner, BFormInput,
  VBTooltip,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import SaveSearchModal from './SaveSearchModal.vue'
import CreateCustomerModal from './CreateCustomerModal.vue'
import ChatImage from './ChatImage.vue'

export default {
  components: {
    BCard, BButton, BTable, BAvatar, BBadge, BPagination, BSpinner, BFormInput,
    SaveSearchModal, CreateCustomerModal, ChatImage,
  },
  directives: { 'b-tooltip': VBTooltip },

  data() {
    const userData = getUserData()
    const PROFILE_KEY = 'chatbot_agent_profile'
    const cached = JSON.parse(localStorage.getItem(PROFILE_KEY) || 'null')
    const agentProfile = cached && cached.id === userData?.id ? cached : null

    return {
      loading: false,
      connectionStatus: '',
      users: [],
      lastRefresh: '',
      refreshTimer: null,
      countdownTimer: null,
      countdown: 30,
      REFRESH_INTERVAL: 30,
      searchQuery: '',
      contentSearchResults: [],
      contentSearching: false,
      _searchDebounceTimer: null,
      highlightedMsgIndex: null,
      _highlightTimer: null,
      activeMatches: [],
      activeMatchIndex: -1,

      // Chat history
      showHistoryPanel: false,
      selectedUser: null,
      historyLoading: false,
      chatMessages: [],

      // Customer profile (bot-built, per conversation)
      customerProfile: null,
      profileOpen: true,
      listPanelWidth: 320,
      isResizing: false,

      // Send message
      sendText: '',
      sending: false,
      showEmoji: false,
      emojiList: [
        '😀','😂','🥰','😍','🤩','😎','🥳','😊','🙂','😉',
        '😢','😭','😤','😠','🤔','🤗','😴','🤒','🥺','😱',
        '👍','👎','👋','🙏','💪','🤝','👏','✌️','🫶','❤️',
        '🔥','⭐','✨','🎉','🎊','💯','✅','❌','🚀','💡',
        '😅','🤣','😇','🥹','😋','😛','🤪','😜','🤑','😏',
        '🫠','🫡','🤫','🤭','😶','😐','😑','🙄','😬','🫤',
        '👀','💀','🫶','💔','💕','💞','💫','🌈','🌟','🍀',
        '🐶','🐱','🐻','🦊','🐼','🐨','🐯','🦁','🐸','🐧',
        '🍕','🍔','🍜','🍣','🍦','🎂','☕','🧋','🍺','🥂',
        '⚽','🏀','🎮','🎵','🎶','📱','💻','🏠','🚗','✈️',
      ],

      // SSE
      sseAbort: null,

      // FCM
      fcmUnsubscribe: null,

      // Bot avatar
      BOT_AVATAR: 'https://digitalnation.sgp1.cdn.digitaloceanspaces.com/findbangkokroom/storage/1773628173458-default.png',

      // Logged-in agent
      userData,
      agentProfile,

      // ── Linked customers ──────────────────────────────────────────────
      linkedCustomers: {},

      // ── Saved searches ───────────────────────────────────────────────
      savedSearchCounts: {},

      // ── Tags ──────────────────────────────────────────────────────────
      tags: [],
      userTags: {},
      tagsLoading: false,
      showTagManager: false,
      showTagPopup: false,
      newTagName: '',
      newTagColor: '#7367f0',
      tagFilterId: null,
      TAG_COLORS: [
        '#7367f0', '#28a745', '#ea5455', '#ff9f43',
        '#00cfe8', '#1e9af3', '#9b59b6', '#e91e63',
      ],
    }
  },

  watch: {
    showEmoji(val) {
      if (val) {
        document.addEventListener('click', this._closeEmojiOnOutsideClick)
      } else {
        document.removeEventListener('click', this._closeEmojiOnOutsideClick)
      }
    },
    totalUnread(val) {
      this.$store.commit('chatbot/SET_UNREAD', val)
    },
    searchQuery(val) {
      clearTimeout(this._searchDebounceTimer)
      const q = val.trim()
      if (q.length < 2) {
        this.contentSearchResults = []
        this.contentSearching = false
        return
      }
      this._searchDebounceTimer = setTimeout(() => this.searchMessageContent(q), 350)
    },
    showTagManager(val) {
      if (val) {
        document.addEventListener('click', this._closeTagManagerOutside)
      } else {
        document.removeEventListener('click', this._closeTagManagerOutside)
      }
    },
    showTagPopup(val) {
      if (val) {
        document.addEventListener('click', this._closeTagPopupOutside)
      } else {
        document.removeEventListener('click', this._closeTagPopupOutside)
      }
    },
  },

  computed: {
    isMobile() {
      return typeof window !== 'undefined' && window.innerWidth < 768
    },
    // The bot-built customer profile as label/value rows, skipping empty fields.
    profileRows() {
      const p = this.customerProfile
      if (!p) return []
      const rows = []
      const money = n => (n == null ? '?' : Number(n).toLocaleString('en-US'))
      if (p.intent && p.intent !== 'unknown') {
        rows.push({ label: 'Looking to', value: { rent: 'Rent', buy: 'Buy', both: 'Rent or buy' }[p.intent] || p.intent })
      }
      if (p.budget_min != null || p.budget_max != null) {
        rows.push({ label: 'Budget', value: `฿${money(p.budget_min)} – ฿${money(p.budget_max)}` })
      }
      if (Array.isArray(p.areas) && p.areas.length) rows.push({ label: 'Areas', value: p.areas.join(', ') })
      if (p.bedrooms) rows.push({ label: 'Bedrooms', value: p.bedrooms })
      if (p.property_type && p.property_type !== 'unknown') rows.push({ label: 'Type', value: p.property_type })
      if (p.move_in) rows.push({ label: 'Move-in', value: p.move_in })
      if (Array.isArray(p.must_haves) && p.must_haves.length) rows.push({ label: 'Must-have', value: p.must_haves.join(', ') })
      if (p.language) rows.push({ label: 'Language', value: p.language.toUpperCase() })
      if (p.notes) rows.push({ label: 'Notes', value: p.notes })
      return rows
    },
    isCustomTagColor() {
      return !this.TAG_COLORS.includes(this.newTagColor)
    },
    filteredUsers() {
      let users = this.users
      if (this.tagFilterId) {
        users = users.filter(u =>
          (this.userTags[u.user_id] || []).some(t => t.id === this.tagFilterId),
        )
      }
      const q = this.searchQuery.trim().toLowerCase()
      if (!q) return users
      const nameMatches = users.filter(u =>
        (u.display_name || '').toLowerCase().includes(q) ||
        (u.user_id || '').toLowerCase().includes(q),
      )
      const nameMatchIds = new Set(nameMatches.map(u => u.user_id))
      const contentMatches = this.contentSearchResults
        .filter(r => !nameMatchIds.has(r.user_id))
        .map(r => users.find(u => u.user_id === r.user_id))
        .filter(Boolean)
      return [...nameMatches, ...contentMatches]
    },
    totalUnread() {
      return this.users.reduce((sum, u) => sum + (u.unread_count || 0), 0)
    },
  },

  mounted() {
    this.fetchAgentProfile()
    this.loadUsers().then(() => this.openConversationFromQuery())
    this.loadTags()
    this.startAutoRefresh()
    this.startSSE()
    this.setupFCM()
  },

  beforeDestroy() {
    this.stopAutoRefresh()
    this.stopSSE()
    this.stopResize()
    clearTimeout(this._searchDebounceTimer)
    clearTimeout(this._highlightTimer)
    if (this.fcmUnsubscribe) this.fcmUnsubscribe()
    document.removeEventListener('click', this._closeEmojiOnOutsideClick)
    document.removeEventListener('click', this._closeTagManagerOutside)
    document.removeEventListener('click', this._closeTagPopupOutside)
  },

  methods: {
    // ── Firebase Cloud Messaging ──────────────────────────────────────────
    async setupFCM() {
      try {
        const permission = await Notification.requestPermission()
        if (permission !== 'granted') return

        const token = await requestFCMToken()
        if (!token) return

        console.log('[FCM] token:', token)
        await chatbotAxios.post('/api/v1/fcm/token', { token }).catch(() => {})

        this.fcmUnsubscribe = onFCMMessage(payload => {
          const userId = payload.data?.userId
          if (document.visibilityState === 'visible' && this.selectedUser?.user_id === userId) return

          const { title, body, icon } = payload.notification || {}
          const n = new Notification(title || 'New message', {
            body:     body || '',
            icon:     icon || '/favicon.ico',
            badge:    '/favicon.ico',
            tag:      userId || 'line-chat',
            renotify: true,
          })
          n.onclick = () => {
            window.focus()
            const user = this.users.find(u => u.user_id === userId)
            if (user) this.openHistory(user)
            n.close()
          }
        })
      } catch (e) {
        console.warn('FCM setup failed:', e.message)
      }
    },

    // ── Agent profile ────────────────────────────────────────────────────
    async fetchAgentProfile() {
      const id = this.userData?.id
      if (!id) return
      const PROFILE_KEY = 'chatbot_agent_profile'
      try {
        const { data } = await axios.get(`users/v1/detail/${id}`)
        const u = data.data
        const profile = {
          id:      u.id,
          name:    `${u.firstname || ''} ${u.lastname || ''}`.trim() || this.userData.username,
          avatar:  u.avatar_pathurl || null,
          initial: (u.firstname || this.userData.username || 'A')[0].toUpperCase(),
        }
        this.agentProfile = profile
        localStorage.setItem(PROFILE_KEY, JSON.stringify(profile))
      } catch {
        if (!this.agentProfile) {
          const name = this.userData?.fullName || this.userData?.username || 'Agent'
          this.agentProfile = {
            id:      this.userData?.id,
            name,
            avatar:  this.userData?.avatar || null,
            initial: name[0].toUpperCase(),
          }
        }
      }
    },

    // ── Users ──────────────────────────────────────────────────────────
    startAutoRefresh() {
      this.countdown = this.REFRESH_INTERVAL
      this.countdownTimer = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown -= 1
        }
      }, 1000)
      this.refreshTimer = setInterval(() => {
        this.loadUsers()
        this.countdown = this.REFRESH_INTERVAL
      }, this.REFRESH_INTERVAL * 1000)
    },

    stopAutoRefresh() {
      clearInterval(this.refreshTimer)
      clearInterval(this.countdownTimer)
    },

    async loadUsers() {
      this.loading = true
      try {
        const { data } = await chatbotAxios.get('/api/v1/conversations')
        this.users = data.data || data
        // Sync userTags cache from tags already included in each conversation row
        const tagCache = { ...this.userTags }
        this.users.forEach(u => {
          if (Array.isArray(u.tags)) tagCache[u.user_id] = u.tags
        })
        this.userTags = tagCache
        this.connectionStatus = 'connected'
        this.lastRefresh = new Date().toLocaleTimeString()
        this.$store.commit('chatbot/SET_UNREAD', this.totalUnread)
        if (this.selectedUser) {
          const updated = this.users.find(u => u.user_id === this.selectedUser.user_id)
          if (updated) this.selectedUser = { ...this.selectedUser, ...updated }
        }
        this.loadLinkedCustomers(this.users.map(u => u.user_id))
      } catch (e) {
        this.connectionStatus = 'error'
        this.showToast('Failed to load users: ' + e.message, 'danger')
      } finally {
        this.loading = false
      }
    },

    async searchMessageContent(q) {
      this.contentSearching = true
      try {
        const { data } = await chatbotAxios.get('/api/v1/conversations/search', { params: { q, limit: 50 } })
        // Ignore stale responses if the query has since changed
        if (this.searchQuery.trim() !== q) return
        this.contentSearchResults = data.data || data || []
      } catch (e) {
        console.warn('Failed to search messages:', e.message)
      } finally {
        this.contentSearching = false
      }
    },

    matchSnippet(userId) {
      const hit = this.contentSearchResults.find(r => r.user_id === userId)
      return hit ? hit.match_snippet : ''
    },

    matchCount(userId) {
      const hit = this.contentSearchResults.find(r => r.user_id === userId)
      return hit ? hit.match_count || 1 : 0
    },

    async setMode(userId, isAgent) {
      try {
        await chatbotAxios.post(`/api/v1/conversations/${userId}/mode`, { manual: isAgent })
        this.showToast(
          isAgent ? `Agent mode ON for ${this.shortId(userId)}` : `Bot mode ON for ${this.shortId(userId)}`,
          'success',
        )
        await this.loadUsers()
      } catch (e) {
        this.showToast('Error: ' + e.message, 'danger')
      }
    },

    // ── Chat history ────────────────────────────────────────────────────
    toggleHistoryPanel() {
      this.showHistoryPanel = !this.showHistoryPanel
    },

    // Opens the conversation named by ?user=<id> (e.g. arriving from the Leads
    // screen's "View chat" button). Falls back to a bare user object if that
    // conversation isn't in the loaded list yet.
    openConversationFromQuery() {
      const userId = this.$route.query.user
      if (!userId) return
      const user = this.users.find(u => u.user_id === userId) || { user_id: userId }
      this.openHistory(user)
    },

    async openHistory(user) {
      this.selectedUser = user
      this.showHistoryPanel = true
      this.chatMessages = []
      this.sendText = ''
      this.historyLoading = true
      this.showTagPopup = false
      this.highlightedMsgIndex = null
      this.activeMatches = []
      this.activeMatchIndex = -1

      // If opened from a message-content search hit, fetch a wider window so
      // the matched message (which may be older than the default page) is included.
      const q = this.searchQuery.trim()
      const searchHit = q ? this.contentSearchResults.find(r => r.user_id === user.user_id) : null

      this.loadUserTags(user.user_id)
      this.loadProfile(user.user_id)
      this.loadLinkedCustomer(user.user_id)
      this.loadSavedSearchCount(user.user_id)
      if (searchHit) this.loadConversationMatches(user.user_id, q)

      const idx = this.users.findIndex(u => u.user_id === user.user_id)
      if (idx !== -1 && this.users[idx].unread_count > 0) {
        this.$set(this.users, idx, { ...this.users[idx], unread_count: 0 })
      }

      try {
        const params = searchHit ? { limit: 500 } : {}
        const { data } = await chatbotAxios.get(`/api/v1/conversations/${user.user_id}/messages`, { params })
        this.chatMessages = this.parseMessages(data.data || data || [])
        const matchIdx = this.locateMessageIndex(searchHit && searchHit.match_created_at, searchHit && searchHit.match_snippet)
        if (matchIdx !== -1) {
          this.scrollToMessage(matchIdx)
        } else {
          this.$nextTick(() => this.scrollToBottom())
        }
        chatbotAxios.post(`/api/v1/conversations/${user.user_id}/read`).catch(() => {})
      } catch (e) {
        this.showToast('Failed to load history: ' + e.message, 'danger')
      } finally {
        this.historyLoading = false
      }
    },

    // Full list of message-content matches within one conversation, so the
    // agent can step through every hit (like the LINE app's in-chat search).
    async loadConversationMatches(userId, q) {
      try {
        const { data } = await chatbotAxios.get(`/api/v1/conversations/${userId}/search`, { params: { q } })
        // Guard against a race: user may have switched chats/query by the time this resolves.
        if (!this.selectedUser || this.selectedUser.user_id !== userId || this.searchQuery.trim() !== q) return
        const matches = data.data || data || []
        this.activeMatches = matches
        this.activeMatchIndex = matches.length - 1 // start at the most recent match, shown in the list snippet
      } catch (e) {
        console.warn('Failed to load conversation matches:', e.message)
      }
    },

    goToMatch(delta) {
      const n = this.activeMatches.length
      if (!n) return
      this.activeMatchIndex = (this.activeMatchIndex + delta + n) % n
      const match = this.activeMatches[this.activeMatchIndex]
      const idx = this.locateMessageIndex(match.created_at, match.content)
      if (idx !== -1) this.scrollToMessage(idx)
    },

    closeHistory() {
      this.showHistoryPanel = false
      this.selectedUser = null
      this.chatMessages = []
      this.customerProfile = null
      this.sendText = ''
      this.showTagPopup = false
      this.activeMatches = []
      this.activeMatchIndex = -1
    },

    // ── Send message ─────────────────────────────────────────────────────
    async sendMessage() {
      const text = this.sendText.trim()
      if (!text || !this.selectedUser || this.sending) return
      this.sending = true
      const agent = this.agentProfile || {}
      const agentId     = agent.id
      const agentName   = agent.name   || 'Agent'
      const agentAvatar = agent.avatar || null
      const agentInitial = agent.initial || agentName[0].toUpperCase()
      this.sendText = ''
      try {
        await chatbotAxios.post(`/api/v1/conversations/${this.selectedUser.user_id}/send`, {
          text,
          agentId,
          agentName,
          agentAvatar,
        })
        this.chatMessages.push({
          role: 'agent',
          text,
          time: this.formatDate(new Date()),
          agentName,
          agentAvatar,
          agentInitial,
        })
        this.$nextTick(() => this.scrollToBottom())
      } catch (e) {
        this.showToast('Failed to send: ' + e.message, 'danger')
      } finally {
        this.sending = false
      }
    },

    // ── SSE real-time ────────────────────────────────────────────────────
    startSSE() {
      const baseURL = process.env.VUE_APP_CHATBOT_URL || 'http://localhost:3000'
      const secret = process.env.VUE_APP_CHATBOT_SECRET || ''
      const controller = new AbortController()
      this.sseAbort = controller

      fetch(`${baseURL}/api/v1/stream`, {
        headers: { 'x-admin-secret': secret },
        signal: controller.signal,
      }).then(res => {
        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        const pump = () => reader.read().then(({ done, value }) => {
          if (done || controller.signal.aborted) return
          const chunk = decoder.decode(value, { stream: true })
          chunk.split('\n').forEach(line => {
            if (!line.startsWith('data:')) return
            try {
              const event = JSON.parse(line.slice(5).trim())
              this.handleSSEEvent(event)
            } catch (_) { /* partial / non-JSON line */ }
          })
          pump()
        }).catch(() => {})
        pump()
      }).catch(() => {})
    },

    stopSSE() {
      if (this.sseAbort) {
        this.sseAbort.abort()
        this.sseAbort = null
      }
    },

    handleSSEEvent(event) {
      // Lead events are consumed by the Leads view, not here.
      if (event.type === 'lead' || event.type === 'lead_status') return

      // Customer-profile update for the open conversation.
      if (event.type === 'profile') {
        if (this.selectedUser && event.userId === this.selectedUser.user_id) {
          this.customerProfile = event.cleared ? null : { ...event }
        }
        return
      }

      const isSelected = this.selectedUser && event.userId === this.selectedUser.user_id
      const isSelf = event.sender_type === 'agent' && event.agent_id === this.agentProfile?.id

      if (isSelected && !isSelf) {
        if (event.content) {
          const trimmedContent = event.content.trim()
          if (!trimmedContent) return
          const agentName   = event.agent_name   || null
          const agentAvatar = event.agent_avatar || null
          this.chatMessages.push({
            role: event.role || 'user',
            ...this.parseContent(trimmedContent),
            time: this.formatDate(event.created_at || new Date()),
            rawCreatedAt: event.created_at || null,
            agentName,
            agentAvatar,
            agentInitial: agentName ? agentName[0].toUpperCase() : null,
            lineMessageId: event.line_message_id || null,
            quotedMessageId: event.quoted_message_id || null,
          })
          this.$nextTick(() => this.scrollToBottom())
        }
      } else if (!isSelected) {
        const idx = this.users.findIndex(u => u.user_id === event.userId)
        if (idx !== -1) {
          const cur = this.users[idx]
          this.$set(this.users, idx, {
            ...cur,
            unread_count: (cur.unread_count || 0) + 1,
            updated_at: new Date().toISOString(),
          })
        } else {
          this.loadUsers()
        }
      }
    },

    parseMessages(raw) {
      return raw
        .filter(msg => msg.content && msg.content.trim())
        .map(msg => {
          const agentName   = msg.agent_name   || null
          const agentAvatar = msg.agent_avatar || null
          return {
            role: msg.role,
            ...this.parseContent(msg.content.trim()),
            time: msg.created_at ? this.formatDate(msg.created_at) : null,
            rawCreatedAt: msg.created_at || null,
            agentName,
            agentAvatar,
            agentInitial: agentName ? agentName[0].toUpperCase() : null,
            lineMessageId: msg.line_message_id || null,
            quotedMessageId: msg.quoted_message_id || null,
          }
        })
    },

    // Resolves a quote-reply's preview from the message it quoted, when that
    // message is loaded in the current chatMessages window. LINE only gives us
    // the quoted message's id, not its content, so this only works if that
    // earlier message is in the same page of history — falls back to a
    // generic "not available" label in the template otherwise (e.g. it scrolled
    // out of the default 100-message window).
    quotedPreview(msg) {
      if (!msg.quotedMessageId) return null
      const original = this.chatMessages.find(m => m.lineMessageId === msg.quotedMessageId)
      if (!original) return null
      const sender = original.role === 'user'
        ? this.contactDisplayName(this.selectedUser)
        : (original.agentName || 'Bot')
      const text = original.type === 'image' ? '📷 Photo' : (original.text || '').trim()
      return { sender, text: text.length > 80 ? text.slice(0, 80) + '…' : text }
    },

    scrollToQuoted(msg) {
      const idx = this.chatMessages.findIndex(m => m.lineMessageId === msg.quotedMessageId)
      if (idx !== -1) this.scrollToMessage(idx)
    },

    // Log entries store an `[image:<lineMessageId>]` marker in place of the
    // real content — the actual bytes are fetched on demand via ChatImage.
    parseContent(content) {
      const match = content.match(/^\[image:(\d+)\]$/)
      if (match) return { type: 'image', imageMessageId: match[1], text: '' }
      return { type: 'text', text: content }
    },

    scrollToBottom() {
      setTimeout(() => {
        const el = this.$refs.chatBody
        if (el) el.scrollTop = el.scrollHeight
      }, 0)
    },

    locateMessageIndex(createdAt, content) {
      const targetTime = createdAt ? new Date(createdAt).getTime() : null
      if (targetTime) {
        const idx = this.chatMessages.findIndex(m => m.rawCreatedAt && new Date(m.rawCreatedAt).getTime() === targetTime)
        if (idx !== -1) return idx
      }
      const snippet = (content || '').trim()
      if (!snippet) return -1
      return this.chatMessages.findIndex(m => m.text === snippet)
    },

    scrollToMessage(index) {
      // Match scrollToBottom's timing (macrotask, not $nextTick) so layout has
      // settled before we measure — matters here because we compute an offset
      // rather than just snapping scrollTop to scrollHeight.
      setTimeout(() => {
        const container = this.$refs.chatBody
        const refEl = this.$refs['msgRow' + index]
        const target = Array.isArray(refEl) ? refEl[0] : refEl
        if (!container || !target) {
          this.scrollToBottom()
          return
        }
        const containerRect = container.getBoundingClientRect()
        const targetRect = target.getBoundingClientRect()
        const offset = (targetRect.top - containerRect.top) - (container.clientHeight / 2) + (targetRect.height / 2)
        container.scrollTop += offset
        clearTimeout(this._highlightTimer)
        this.highlightedMsgIndex = index
        this._highlightTimer = setTimeout(() => { this.highlightedMsgIndex = null }, 2200)
      }, 0)
    },

    // ── Resize ──────────────────────────────────────────────────────────
    startResize(e) {
      this.isResizing = true
      this._startX = e.clientX
      this._startWidth = this.listPanelWidth
      document.addEventListener('mousemove', this.onResize)
      document.addEventListener('mouseup', this.stopResize)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    },

    onResize(e) {
      if (!this.isResizing) return
      const delta = e.clientX - this._startX
      const newWidth = Math.min(500, Math.max(220, this._startWidth + delta))
      this.listPanelWidth = newWidth
    },

    stopResize() {
      this.isResizing = false
      document.removeEventListener('mousemove', this.onResize)
      document.removeEventListener('mouseup', this.stopResize)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    },

    rowClass(item) {
      if (this.selectedUser && item && item.user_id === this.selectedUser.user_id) return 'row--selected'
      return ''
    },

    toggleEmoji() {
      this.showEmoji = !this.showEmoji
    },

    _closeEmojiOnOutsideClick() {
      this.showEmoji = false
    },

    insertEmoji(emoji) {
      const input = this.$refs.sendInput?.$el?.querySelector('input') || this.$refs.sendInput?.$el
      if (input) {
        const start = input.selectionStart ?? this.sendText.length
        const end   = input.selectionEnd   ?? this.sendText.length
        this.sendText = this.sendText.slice(0, start) + emoji + this.sendText.slice(end)
        this.$nextTick(() => {
          const pos = start + emoji.length
          input.focus()
          input.setSelectionRange(pos, pos)
        })
      } else {
        this.sendText += emoji
      }
      this.showEmoji = false
    },

    // ── Linked customers ─────────────────────────────────────────────────
    async loadLinkedCustomer(userId) {
      try {
        const { data } = await axios.post('users/v1/lists', { line_contact: userId, perpage: 1, offset: 0 })
        const match = (data.data || [])[0] || null
        this.$set(this.linkedCustomers, userId, match)
      } catch (e) {
        console.warn('Failed to check linked customer:', e.message)
      }
    },

    async loadLinkedCustomers(userIds) {
      const ids = [...new Set(userIds)].filter(Boolean)
      if (!ids.length) return
      try {
        const { data } = await axios.post('users/v1/lists', { line_contact: ids, perpage: ids.length, offset: 0 })
        const matches = {}
        const rows = data.data || []
        rows.forEach(customer => {
          if (customer.line_contact) matches[customer.line_contact] = customer
        })
        this.linkedCustomers = matches
      } catch (e) {
        console.warn('Failed to load linked customers:', e.message)
      }
    },

    getLinkedCustomer(userId) {
      return this.linkedCustomers[userId]
    },

    linkedCustomerName(userId) {
      const customer = this.linkedCustomers[userId]
      if (!customer) return ''
      return [customer.firstname, customer.lastname].filter(Boolean).join(' ') || 'Unnamed'
    },

    contactDisplayName(item) {
      if (!item) return 'Unknown'
      return this.linkedCustomerName(item.user_id) || item.display_name || 'Unknown'
    },

    onCustomerCreated(message, customer) {
      this.showToast(message, 'success')
      if (this.selectedUser && customer) {
        this.$set(this.linkedCustomers, this.selectedUser.user_id, customer)
      }
    },

    // ── Saved searches ───────────────────────────────────────────────────
    async loadSavedSearchCount(userId) {
      try {
        const { data } = await axios.get(`users/v1/saved-search/agent-list/${userId}`)
        const list = data.data || data || []
        this.$set(this.savedSearchCounts, userId, list.length)
      } catch (e) {
        console.warn('Failed to load saved search count:', e.message)
      }
    },

    hasSavedSearch(userId) {
      return (this.savedSearchCounts[userId] || 0) > 0
    },

    onSavedSearchChanged(message) {
      this.showToast(message, 'success')
      if (this.selectedUser) this.loadSavedSearchCount(this.selectedUser.user_id)
    },

    // ── Tags ────────────────────────────────────────────────────────────
    toggleTagManager() {
      this.showTagManager = !this.showTagManager
      if (this.showTagManager) this.showTagPopup = false
    },

    toggleTagPopup() {
      this.showTagPopup = !this.showTagPopup
      if (this.showTagPopup) this.showTagManager = false
    },

    _closeTagManagerOutside() {
      this.showTagManager = false
    },

    _closeTagPopupOutside() {
      this.showTagPopup = false
    },

    async loadTags() {
      this.tagsLoading = true
      try {
        const { data } = await chatbotAxios.get('/api/v1/tags')
        this.tags = data.data || data || []
      } catch (e) {
        console.warn('Failed to load tags:', e.message)
      } finally {
        this.tagsLoading = false
      }
    },

    async loadUserTags(userId) {
      try {
        const { data } = await chatbotAxios.get(`/api/v1/conversations/${userId}/tags`)
        this.$set(this.userTags, userId, data.data || data || [])
      } catch (e) {
        console.warn('Failed to load user tags:', e.message)
      }
    },

    async loadProfile(userId) {
      this.customerProfile = null
      try {
        const { data } = await chatbotAxios.get(`/api/v1/conversations/${userId}/profile`)
        this.customerProfile = data.data || null
      } catch (e) {
        console.warn('Failed to load customer profile:', e.message)
      }
    },

    async resetProfile() {
      if (!this.selectedUser) return
      try {
        await chatbotAxios.delete(`/api/v1/conversations/${this.selectedUser.user_id}/profile`)
        this.customerProfile = null
        this.showToast('Customer profile reset')
      } catch (e) {
        this.showToast('Failed to reset profile: ' + e.message, 'danger')
      }
    },

    async createTag() {
      const name = this.newTagName.trim()
      if (!name) return
      try {
        const { data } = await chatbotAxios.post('/api/v1/tags', { name, color: this.newTagColor })
        const tag = data.data || data
        this.tags = [...this.tags, tag]
        this.newTagName = ''
      } catch (e) {
        this.showToast('Failed to create tag: ' + e.message, 'danger')
      }
    },

    async deleteTag(id) {
      try {
        await chatbotAxios.delete(`/api/v1/tags/${id}`)
        this.tags = this.tags.filter(t => t.id !== id)
        // Evict deleted tag from all cached user tag lists
        const updated = {}
        Object.keys(this.userTags).forEach(uid => {
          updated[uid] = this.userTags[uid].filter(t => t.id !== id)
        })
        this.userTags = updated
      } catch (e) {
        this.showToast('Failed to delete tag: ' + e.message, 'danger')
      }
    },

    async toggleUserTag(userId, tagId) {
      const current = this.userTags[userId] || []
      const isTagged = current.some(t => t.id === tagId)
      if (isTagged) {
        try {
          await chatbotAxios.delete(`/api/v1/conversations/${userId}/tags/${tagId}`)
          this.$set(this.userTags, userId, current.filter(t => t.id !== tagId))
        } catch (e) {
          this.showToast('Failed to remove tag: ' + e.message, 'danger')
        }
      } else {
        try {
          await chatbotAxios.post(`/api/v1/conversations/${userId}/tags`, { tagId })
          const tag = this.tags.find(t => t.id === tagId)
          if (tag) this.$set(this.userTags, userId, [...current, tag])
        } catch (e) {
          this.showToast('Failed to add tag: ' + e.message, 'danger')
        }
      }
    },

    isUserTagged(userId, tagId) {
      return (this.userTags[userId] || []).some(t => t.id === tagId)
    },

    getUserTagObjects(userId) {
      return this.userTags[userId] || []
    },

    // ── Helpers ──────────────────────────────────────────────────────────
    copyId(id) {
      navigator.clipboard.writeText(id).then(() => this.showToast('Copied!', 'success'))
    },
    avatarInitial(name) { return name ? name[0].toUpperCase() : '?' },
    shortId(id) { return id.length > 12 ? id.slice(0, 10) + '…' : id },
    formatDate(raw) {
      if (!raw) return '-'
      const d = new Date(raw)
      const dd = String(d.getDate()).padStart(2, '0')
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const yy = String(d.getFullYear()).slice(-2)
      const hh = String(d.getHours()).padStart(2, '0')
      const min = String(d.getMinutes()).padStart(2, '0')
      return `${dd}/${mm}/${yy}, ${hh}:${min}`
    },
    showToast(message, variant = 'success') {
      this.$toast({
        component: ToastificationContent,
        props: { title: message, icon: variant === 'success' ? 'CheckIcon' : 'AlertCircleIcon', variant },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
// ── Page layout ──────────────────────────────────────────────────────────────
.chatbot-page {
  display: flex;
  height: calc(100vh - 180px);
  min-height: 500px;
  border-radius: 0.428rem;
  overflow: hidden;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);

  @media (max-width: 767px) {
    height: calc(100dvh - 130px);
    min-height: unset;
    flex-direction: column;
    border-radius: 0.428rem;
    box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
  }
}

// ── Left panel ────────────────────────────────────────────────────────────────
.list-panel {
  width: 320px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #ebe9f1;
  background: #fff;
  overflow: hidden;

  @media (max-width: 767px) {
    width: 100% !important;
    border-right: none;
    border-bottom: none;
    flex: 1;
    height: 0;
  }
}

.list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px 12px;
  flex-shrink: 0;
}

.list-search {
  position: relative;
  padding: 0 12px 10px;
  flex-shrink: 0;
}
.list-search__icon {
  position: absolute;
  left: 24px;
  top: 50%;
  transform: translateY(-65%);
  color: #adb5bd;
  pointer-events: none;
}
.list-search__input {
  width: 100%;
  border: 1px solid #e8e8ee;
  border-radius: 20px;
  padding: 7px 14px 7px 34px;
  font-size: 0.92rem;
  background: #f7f7fc;
  outline: none;
  color: #3d3d3d;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    border-color: #7367f0;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(115,103,240,0.12);
  }
}

.contact-list {
  flex: 1;
  overflow-y: auto;
}

.contact-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  color: #adb5bd;
  font-size: 0.92rem;
  text-align: center;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f5f5f7;
  transition: background 0.12s;
  position: relative;

  &:hover { background: #f8f7ff; }

  &--active {
    background: #eeebff;
    border-left: 3px solid #7367f0;
    padding-left: 13px;

    &:hover { background: #e8e5ff; }
  }

  &--unread .contact-name {
    font-weight: 700;
    color: #1e1e2d;
  }
}

.contact-avatar-wrap {
  position: relative;
  flex-shrink: 0;
}

.contact-body {
  flex: 1;
  min-width: 0;
}

.contact-row1 {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 6px;
  margin-bottom: 4px;
}

.contact-name {
  font-size: 1rem;
  font-weight: 500;
  color: #3d3d3d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.contact-name__linked-icon {
  color: #28a745;
  margin-right: 2px;
  flex-shrink: 0;
}

.contact-date {
  font-size: 0.78rem;
  color: #adb5bd;
  white-space: nowrap;
  flex-shrink: 0;
}

.contact-row2 {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.contact-uid {
  font-size: 0.78rem;
  color: #c4c4cc;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: monospace;
  margin-left: auto;
}

.contact-match-snippet {
  display: flex;
  align-items: center;
  margin-top: 4px;
  font-size: 0.78rem;
  font-style: italic;
  color: #8f8fa3;
  gap: 4px;
}

.contact-match-snippet__text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.contact-match-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #e0dff5;
  color: #7367f0;
  border-radius: 10px;
  font-size: 0.7rem;
  font-style: normal;
  font-weight: 700;
  min-width: 16px;
  height: 16px;
  padding: 0 5px;
  flex-shrink: 0;
  margin-left: auto;
}

.list-footer {
  padding: 8px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #f0f0f5;
  flex-shrink: 0;
  background: #fafafa;
}

.auto-refresh-indicator {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #adb5bd;
  font-size: 0.8rem;
}

.auto-refresh-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-block;

  &--live {
    background: #28a745;
    box-shadow: 0 0 0 0 rgba(40, 167, 69, 0.4);
    animation: pulse-green 2s infinite;
  }

  &--loading {
    background: #f0ad4e;
    animation: none;
  }
}

@keyframes pulse-green {
  0%   { box-shadow: 0 0 0 0 rgba(40, 167, 69, 0.4); }
  70%  { box-shadow: 0 0 0 6px rgba(40, 167, 69, 0); }
  100% { box-shadow: 0 0 0 0 rgba(40, 167, 69, 0); }
}

// ── Unread badge on avatars ───────────────────────────────────────────────────
.unread-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  background: #ea5455;
  color: #fff;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 700;
  min-width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 3px;
  line-height: 1;
  border: 2px solid #fff;
  pointer-events: none;
}

// ── Toolbar unread total badge ────────────────────────────────────────────────
.unread-total {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #ea5455;
  color: #fff;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 700;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  line-height: 1;
}

// ── Connection badge ──────────────────────────────────────────────────────────
.connection-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
}
.badge-connected { background: #d4edda; color: #155724; }
.badge-error     { background: #f8d7da; color: #721c24; }
.dot { width: 6px; height: 6px; border-radius: 50%; display: inline-block; flex-shrink: 0; }
.badge-connected .dot { background: #28a745; }
.badge-error .dot     { background: #dc3545; }

// ── Mode badge ────────────────────────────────────────────────────────────────
.mode-badge {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 8px; border-radius: 20px;
  font-size: 0.8rem; font-weight: 700; white-space: nowrap; flex-shrink: 0;
}
.mode-bot   { background: #d4edda; color: #155724; }
.mode-agent { background: #e9ecef; color: #6c757d; }
.mode-dot   { width: 5px; height: 5px; border-radius: 50%; display: inline-block; flex-shrink: 0; }
.mode-bot .mode-dot   { background: #28a745; }
.mode-agent .mode-dot { background: #adb5bd; }

// ── Spin animation ────────────────────────────────────────────────────────────
.spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

// ════════════════════════════════════════════════════════════════════════════════
// TAG SYSTEM
// ════════════════════════════════════════════════════════════════════════════════

// Shared tag pill
.tag-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  flex-shrink: 0;
}

.tag-pill--sm {
  padding: 1px 6px;
  font-size: 0.75rem;
}

.tag-more {
  font-size: 0.75rem;
  color: #adb5bd;
  flex-shrink: 0;
}

// ── Tag manager dropdown (list panel) ────────────────────────────────────────
.tag-manager-wrap {
  position: relative;
}

.tag-manager-panel {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  width: 240px;
  background: #fff;
  border: 1px solid #e8e8ee;
  border-radius: 12px;
  box-shadow: 0 8px 28px rgba(0,0,0,0.14);
  z-index: 1000;
  overflow: hidden;
}

.tag-manager-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px 8px;
  border-bottom: 1px solid #f0f0f5;
}

.tag-manager-close {
  border: none;
  background: transparent;
  font-size: 1.1rem;
  color: #adb5bd;
  cursor: pointer;
  line-height: 1;
  padding: 0 2px;

  &:hover { color: #6e6b7b; }
}

.tag-list-manage {
  max-height: 150px;
  overflow-y: auto;
  padding: 6px 10px;
}

.tag-manage-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 2px;
  gap: 8px;
}

.tag-delete-btn {
  border: none;
  background: transparent;
  color: #adb5bd;
  font-size: 1rem;
  cursor: pointer;
  line-height: 1;
  padding: 0 3px;
  flex-shrink: 0;

  &:hover { color: #ea5455; }
}

.tag-create-form {
  padding: 10px 12px 12px;
  border-top: 1px solid #f0f0f5;
}

.tag-color-swatches {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.tag-swatch {
  width: 22px;
  height: 22px;
  min-width: 22px;
  border-radius: 50%;
  border: none;
  padding: 0;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
  flex-shrink: 0;
  display: block;
  transition: transform 0.1s, outline 0.1s;

  &:hover { transform: scale(1.15); }
}

.tag-swatch-custom {
  position: relative;
  overflow: hidden;
  border: 1.5px dashed #c4c4cc;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #adb5bd;
  background: #fff;

  &:hover { border-color: #7367f0; color: #7367f0; }
}

.tag-swatch-custom-input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  padding: 0;
  border: none;
  opacity: 0;
  cursor: pointer;
}

.tag-create-row {
  display: flex;
  gap: 6px;
}

.tag-create-input {
  flex: 1;
  border: 1px solid #e8e8ee;
  border-radius: 8px;
  padding: 5px 10px;
  font-size: 0.82rem;
  outline: none;
  color: #3d3d3d;

  &:focus {
    border-color: #7367f0;
    box-shadow: 0 0 0 2px rgba(115,103,240,0.12);
  }
}

.tag-create-btn {
  border: none;
  background: #7367f0;
  color: #fff;
  border-radius: 8px;
  padding: 5px 12px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;

  &:hover:not(:disabled) { background: #5e52d6; }
  &:disabled { opacity: 0.5; cursor: default; }
}

// ── Tag filter bar ────────────────────────────────────────────────────────────
.tag-filter-bar {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 0 12px 10px;
  flex-shrink: 0;
  flex-wrap: wrap;
}

.tag-filter-btn {
  border: 1px solid #e0dff5;
  background: transparent;
  color: #6e6b7b;
  border-radius: 20px;
  padding: 3px 10px;
  font-size: 0.73rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;

  &:hover { border-color: #7367f0; color: #7367f0; }

  &--all-active {
    background: #eeebff;
    border-color: #7367f0;
    color: #7367f0;
    font-weight: 700;
  }
}

// ── Tag assign dropdown (history panel header) ────────────────────────────────
.tag-assign-wrap {
  position: relative;
}

.tag-assign-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  width: 200px;
  background: #fff;
  border: 1px solid #e8e8ee;
  border-radius: 12px;
  box-shadow: 0 8px 28px rgba(0,0,0,0.14);
  z-index: 1000;
  overflow: hidden;
}

.tag-assign-header {
  padding: 9px 14px 7px;
  font-size: 0.8rem;
  font-weight: 700;
  color: #3d3d3d;
  border-bottom: 1px solid #f0f0f5;
}

.tag-assign-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  cursor: pointer;
  margin: 0;
  transition: background 0.1s;

  &:hover { background: #f8f7ff; }

  input[type="checkbox"] {
    cursor: pointer;
    flex-shrink: 0;
    accent-color: #7367f0;
  }
}

// ── Tags in history header ────────────────────────────────────────────────────
.header-tags {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

// ── Linked customer info in history header ────────────────────────────────────
.linked-customer-info {
  display: flex;
  align-items: center;
  color: #28a745;
  font-size: 0.75rem;
  font-weight: 600;
  margin-top: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

// ════════════════════════════════════════════════════════════════════════════════
// CHAT HISTORY PANEL
// ════════════════════════════════════════════════════════════════════════════════

// Drag handle between list and sidebar
.resize-handle {
  width: 20px;
  flex-shrink: 0;
  cursor: col-resize;
  background: transparent;
  position: relative;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;

  &:hover {
    background: rgba(115, 103, 240, 0.08);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 3px;
    border-radius: 3px;
    background: #dee2e6;
    transition: background 0.2s, width 0.2s;
  }

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 4px;
    height: 32px;
    border-radius: 2px;
    background:
      radial-gradient(circle, #adb5bd 2px, transparent 2px) center 0 / 4px 8px,
      radial-gradient(circle, #adb5bd 2px, transparent 2px) center 8px / 4px 8px,
      radial-gradient(circle, #adb5bd 2px, transparent 2px) center 16px / 4px 8px,
      radial-gradient(circle, #adb5bd 2px, transparent 2px) center 24px / 4px 8px;
    background-repeat: no-repeat;
    transition: background 0.2s;
  }

  &:hover::before,
  &:active::before {
    background: #7367f0;
    width: 4px;
  }

  &:hover::after,
  &:active::after {
    background:
      radial-gradient(circle, #7367f0 2px, transparent 2px) center 0 / 4px 8px,
      radial-gradient(circle, #7367f0 2px, transparent 2px) center 8px / 4px 8px,
      radial-gradient(circle, #7367f0 2px, transparent 2px) center 16px / 4px 8px,
      radial-gradient(circle, #7367f0 2px, transparent 2px) center 24px / 4px 8px;
    background-repeat: no-repeat;
  }

  @media (max-width: 767px) { display: none; }
}

.history-panel {
  flex: 1;
  min-width: 0;
  background: #fff;
  border-radius: 0;
  box-shadow: none;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: unset;
  position: static;
  overflow: hidden;

  @media (max-width: 767px) {
    position: fixed;
    left: 0; right: 0; bottom: 0;
    width: 100%;
    max-width: 100%;
    height: 92dvh;
    min-height: unset;
    border-radius: 20px 20px 0 0;
    z-index: 1030;
    transform: translateY(100%);
    transition: transform 0.35s cubic-bezier(0.32, 0.72, 0, 1);
    box-shadow: 0 -4px 32px rgba(0, 0, 0, 0.18);

    &.is-open { transform: translateY(0); }
  }
}

.history-no-selection {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
  text-align: center;
  color: #6e6b7b;
}

.history-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1029;
}

.sheet-handle {
  width: 40px;
  height: 5px;
  background: #dee2e6;
  border-radius: 3px;
  margin: 10px auto 4px;
  flex-shrink: 0;
  cursor: pointer;
  transition: background 0.2s;

  &:hover { background: #adb5bd; }
}

.history-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid #ebe9f1;
  flex-shrink: 0;
  background: #fff;
}

// ── Search match navigator (inside history panel) ────────────────────────────
.search-nav {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 18px;
  border-bottom: 1px solid #ebe9f1;
  background: #f8f7ff;
  flex-shrink: 0;
}

.search-nav__icon { color: #7367f0; flex-shrink: 0; }

.search-nav__count {
  font-size: 0.8rem;
  font-weight: 600;
  color: #6e6b7b;
  flex-shrink: 0;
}

.search-nav__btns {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: auto;
}

.search-nav__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: #7367f0;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover { background: #e8e5ff; }
}

.mode-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 9px 16px;
  font-size: 0.88rem;
  font-weight: 500;
  flex-shrink: 0;
  gap: 8px;
}
.mode-banner--bot   { background: #3d3d3d; color: #fff; }
.mode-banner--agent { background: #1a5c30; color: #fff; }

.mode-banner__label {
  display: flex;
  align-items: center;
  opacity: 0.95;
  gap: 6px;
}

.mode-banner__btn {
  flex-shrink: 0;
  background: transparent;
  border: 1.5px solid rgba(255,255,255,0.65);
  color: #fff;
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;

  &:hover { background: rgba(255,255,255,0.18); }
}

// ── Customer profile strip ──────────────────────────────────────────────────
.cust-profile {
  flex-shrink: 0;
  border-bottom: 1px solid #ebe9f1;
  background: #f8f8fc;
}

.cust-profile__toggle {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 16px;
  background: transparent;
  border: none;
  font-size: 0.82rem;
  font-weight: 700;
  color: #6e6b7b;
  cursor: pointer;

  &:hover { color: #7367f0; }
}

.cust-profile__body {
  padding: 4px 16px 10px;
}

.cust-profile__row {
  display: flex;
  gap: 10px;
  padding: 3px 0;
  font-size: 0.84rem;
  line-height: 1.4;
}

.cust-profile__label {
  flex-shrink: 0;
  width: 88px;
  color: #a0a0b8;
  font-weight: 600;
}

.cust-profile__value {
  color: #3d3d3d;
  word-break: break-word;
}

.cust-profile__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px dashed #e0dff0;
  font-size: 0.75rem;
}

.cust-profile__reset {
  background: transparent;
  border: 1px solid #e0dff5;
  color: #ea5455;
  border-radius: 20px;
  padding: 2px 12px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;

  &:hover { background: #fdeaea; }
}

// ── Messages body ─────────────────────────────────────────────────────────────
.history-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 16px;
  background-color: #efeef4;
  background-image:
    radial-gradient(circle, rgba(115,103,240,0.08) 1px, transparent 1px);
  background-size: 28px 28px;
}

.msg-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.empty-history {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
}

.msg-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  margin-bottom: 2px;

  & + .msg-row--user.after-bot,
  & + .msg-row--bot.after-user {
    margin-top: 10px;
  }
}
.msg-row--user { flex-direction: row; }
.msg-row--bot  { flex-direction: row-reverse; }

.msg-row--highlight .msg-bubble {
  animation: msg-highlight-flash 2.2s ease-out;
}
@keyframes msg-highlight-flash {
  0%   { box-shadow: 0 0 0 3px rgba(255, 193, 7, 0.9); }
  70%  { box-shadow: 0 0 0 3px rgba(255, 193, 7, 0.5); }
  100% { box-shadow: 0 0 0 0 rgba(255, 193, 7, 0); }
}

.msg-avatar { flex-shrink: 0; }

.msg-content {
  max-width: 75%;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.msg-content--user { align-items: flex-start; }
.msg-content--bot  { align-items: flex-end; }

.msg-bubble {
  padding: 10px 14px;
  border-radius: 18px;
  font-size: 1rem;
  line-height: 1.55;
  word-break: break-word;
  position: relative;
}

.msg-text {
  white-space: pre-wrap;
}

// Quote-reply preview — shows what a LINE quote-reply was responding to.
.msg-quote {
  display: flex;
  gap: 8px;
  margin-bottom: 6px;
  padding: 4px 8px;
  border-radius: 8px;
  overflow: hidden;
}
.msg-quote--clickable {
  cursor: pointer;
}
.msg-quote__bar {
  flex-shrink: 0;
  width: 3px;
  border-radius: 2px;
}
.msg-quote__body {
  min-width: 0;
  overflow: hidden;
}
.msg-quote__sender {
  font-size: 0.72rem;
  font-weight: 700;
  margin-bottom: 1px;
}
.msg-quote__text {
  font-size: 0.82rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bubble-user .msg-quote {
  background: rgba(45, 45, 58, 0.06);
}
.bubble-user .msg-quote__bar { background: #7367f0; }
.bubble-user .msg-quote__sender { color: #7367f0; }
.bubble-user .msg-quote__text { color: #6e6b7b; }

.bubble-bot .msg-quote {
  background: rgba(255, 255, 255, 0.14);
}
.bubble-bot .msg-quote__bar { background: #fff; }
.bubble-bot .msg-quote__sender { color: #fff; }
.bubble-bot .msg-quote__text { color: rgba(255,255,255,0.85); }

.bubble-user {
  background: #ffffff;
  color: #2d2d3a;
  border-bottom-left-radius: 5px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.12);
}

.bubble-bot {
  background: #7367f0;
  color: #fff;
  border-bottom-right-radius: 5px;
  box-shadow: 0 1px 3px rgba(115,103,240,0.35);
}

:deep(.bot-avatar) {
  background: #fff !important;
  border: 1.5px solid #e0dffe;
}

.msg-agent-name {
  font-size: 0.78rem;
  font-weight: 700;
  color: #7367f0;
  text-align: right;
  margin-bottom: 3px;
  padding-right: 2px;
  letter-spacing: 0.02em;
}

.msg-time {
  font-size: 0.75rem;
  color: #a0a0b8;
  margin-top: 3px;
}
.msg-time--left  { text-align: left;  padding-left: 2px; }
.msg-time--right { text-align: right; padding-right: 2px; }

// ── Footer / input bar ────────────────────────────────────────────────────────
.history-footer {
  padding: 12px 14px;
  padding-bottom: max(12px, env(safe-area-inset-bottom));
  border-top: 1px solid #ebe9f1;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  background: #fff;
}

.send-input {
  flex: 1;
  border-radius: 24px !important;
  border-color: #e0dff5 !important;
  padding-left: 16px !important;
  padding-right: 16px !important;
  background: #f7f7fc !important;
  font-size: 1rem !important;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    border-color: #7367f0 !important;
    box-shadow: 0 0 0 3px rgba(115,103,240,0.15) !important;
    background: #fff !important;
  }
}

.send-btn {
  flex-shrink: 0;
  border-radius: 24px !important;
  padding-left: 18px !important;
  padding-right: 18px !important;
  font-weight: 600 !important;
  letter-spacing: 0.02em;
}

// ── Emoji picker ──────────────────────────────────────────────────────────────
.emoji-wrap {
  position: relative;
  flex-shrink: 0;
}

.emoji-trigger {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  font-size: 1.3rem;
  cursor: pointer;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
  padding: 0;
  line-height: 1;

  &:hover { background: rgba(115,103,240,0.1); }
}

.emoji-picker {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  width: 280px;
  background: #fff;
  border: 1px solid #e8e8ee;
  border-radius: 14px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.14);
  padding: 10px;
  z-index: 999;

  @media (max-width: 400px) {
    width: 240px;
    left: -8px;
  }
}

.emoji-grid {
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 2px;

  @media (max-width: 400px) {
    grid-template-columns: repeat(8, 1fr);
  }
}

.emoji-btn {
  border: none;
  background: transparent;
  font-size: 1.2rem;
  padding: 4px 2px;
  cursor: pointer;
  border-radius: 6px;
  line-height: 1;
  transition: background 0.1s, transform 0.1s;

  &:hover {
    background: #f0eeff;
    transform: scale(1.25);
  }
}
</style>
